import { useState } from "react";
import { useFetch, apiPost, fmtDate } from "@/lib/shieldx";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, Panel } from "@/components/shieldx/Primitives";
import { StatusBadge, RiskBadge, DemoTag } from "@/components/shieldx/Badges";
import { Settings as SettingsIcon, ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  ["super_admin", "Platform Super Admin", "Full platform authority. All privileged actions audited."],
  ["org_owner", "Organization Owner", "Owns org policies, assets and emergency controls."],
  ["security_admin", "Security Administrator", "Manages policies, integrations and agents."],
  ["security_analyst", "Security Analyst", "Investigates incidents; cannot run high-impact emergency actions."],
  ["incident_responder", "Incident Responder", "Can contain, isolate and trigger emergency controls."],
  ["auditor", "Auditor", "Read-only access to audit logs and reports."],
  ["individual", "Individual", "Simplified personal website protection."],
];

const ACTIONS = ["API access", "Database write", "Production deployment", "Credential access", "Data export", "Permission escalation", "Payment action"];

export default function SettingsPage() {
  const { user } = useAuth();
  const authz = useFetch("/authorizations");
  const [action, setAction] = useState("Database write");
  const [risk, setRisk] = useState("HIGH");
  const [inScope, setInScope] = useState(true);
  const [busy, setBusy] = useState(false);

  const evaluate = async () => {
    setBusy(true);
    try { const r = await apiPost("/authorizations/evaluate", { action, risk, in_scope: inScope, actor: user?.name }); toast.success(`Gateway decision: ${r.decision} — ${r.reason}`); authz.reload(); }
    catch { toast.error("Evaluation failed"); } finally { setBusy(false); }
  };

  if (authz.loading) return <Loader label="Loading settings" />;
  const recent = [...authz.data].sort((a, b) => (b.evaluated_at || "").localeCompare(a.evaluated_at || "")).slice(0, 10);

  return (
    <div>
      <PageHeader icon={SettingsIcon} title="Settings"
        subtitle="Identity, roles, authorization gateway and platform self-protection." actions={<DemoTag />} />

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Your Identity">
          <dl className="space-y-2.5 text-sm">
            {[["Name", user?.name],["Email", user?.email],["Role", user?.role],["Organization", user?.org_id],["Auth Provider", user?.auth_provider],["MFA", user?.mfa_enabled ? "Enabled" : "Not enabled"]].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between border-b border-slate-800/60 pb-2">
                <dt className="font-mono text-[11px] uppercase tracking-wider text-slate-500">{k}</dt>
                <dd className="font-mono text-xs text-slate-200">{v || "—"}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-3 text-xs text-slate-500">Authentication ≠ Authorization. Identity establishes who you are; policy decides what you may do.</p>
        </Panel>

        <Panel title="Universal Authorization Gateway" desc="Evaluate a sensitive action against identity, policy and risk">
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs text-slate-400">Action</label>
              <select value={action} onChange={(e) => setAction(e.target.value)} data-testid="authz-action" className="h-10 w-full rounded-lg border border-slate-800 bg-slate-900/70 px-3 text-sm text-slate-100">
                {ACTIONS.map((a) => <option key={a}>{a}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs text-slate-400">Risk</label>
                <select value={risk} onChange={(e) => setRisk(e.target.value)} data-testid="authz-risk" className="h-10 w-full rounded-lg border border-slate-800 bg-slate-900/70 px-3 text-sm text-slate-100">
                  {["LOW", "MEDIUM", "HIGH", "CRITICAL"].map((r) => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-400">Within approved scope?</label>
                <select value={inScope ? "yes" : "no"} onChange={(e) => setInScope(e.target.value === "yes")} data-testid="authz-scope" className="h-10 w-full rounded-lg border border-slate-800 bg-slate-900/70 px-3 text-sm text-slate-100">
                  <option value="yes">Yes</option><option value="no">No</option>
                </select>
              </div>
            </div>
            <button onClick={evaluate} disabled={busy} data-testid="authz-evaluate" className="w-full rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60">Evaluate Authorization</button>
          </div>
          <div className="mt-4">
            <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-slate-500">Recent Decisions</p>
            <div className="space-y-1.5">
              {recent.map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm text-slate-200">{d.action}</p>
                    <p className="font-mono text-[11px] text-slate-500">{d.actor} · {fmtDate(d.evaluated_at)}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5"><RiskBadge value={d.risk} /><StatusBadge value={d.decision} /></div>
                </div>
              ))}
            </div>
          </div>
        </Panel>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Panel title="Roles & Access (RBAC + ABAC)">
          <div className="space-y-2">
            {ROLES.map(([key, name, desc]) => (
              <div key={key} className={`rounded-lg border px-3 py-2.5 ${user?.role === key ? "border-blue-500/40 bg-blue-600/10" : "border-slate-800 bg-slate-900/60"}`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-200">{name}</p>
                  <span className="font-mono text-[10px] text-slate-500">{key}</span>
                </div>
                <p className="mt-0.5 text-xs text-slate-500">{desc}</p>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Platform Self-Protection">
          <div className="space-y-3 text-sm text-slate-300">
            <div className="flex items-start gap-2.5"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" /> ShieldX is treated as a protected critical system with a separate management plane.</div>
            <div className="flex items-start gap-2.5"><Lock className="mt-0.5 h-4 w-4 shrink-0 text-blue-400" /> All privileged administrative operations are auditable — no unrestricted invisible access.</div>
            <div className="flex items-start gap-2.5"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" /> The AI analysis layer cannot grant itself permissions, disable emergency controls, or delete immutable audit logs.</div>
            <div className="flex items-start gap-2.5"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" /> If AI analysis is unavailable, deterministic detection and authorization continue operating.</div>
          </div>
          <p className="mt-4 font-mono text-[11px] uppercase tracking-widest text-slate-600">AI may evolve without limits. Authority does not.</p>
        </Panel>
      </div>
    </div>
  );
}
